import { CanAccess } from '@modules/common/components/CanAccess';
import { InputTags } from '@modules/common/components/InputTags';
import { TrackedKeywordFormPhrasesSVGIcon } from '@modules/common/components/SVGIcons';
import { SelectCategoryMenu } from '@modules/common/components/SelectCategoryMenu';
import { TrackedKeywordPhrasesControlLabel } from '@modules/taxonomyV2/components/TrackedKeywordForm/TrackedKeywordPhrasesControlLabel';
import {
  EXCLUDED_PHRASES_TOOLTIP_CONTENT,
  INCLUDED_PHRASES_TOOLTIP_CONTENT,
} from '@modules/taxonomyV2/constants';
import {
  AddTrackedKeywordFormData,
  EditTrackedKeywordFormData,
  FormStateOptions,
  TrackedKeywordFormStateErrors,
} from '@modules/taxonomyV2/types';
import { AddRounded, RemoveRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Button, Stack, TextField, Typography } from '@mui/material';
import {
  Category,
  PermissionOperation,
  PermissionResource,
} from 'generated/graphql';
import { useRef } from 'react';

interface CreateOrUpdateTrackedKeywordFormProps {
  formState: AddTrackedKeywordFormData | EditTrackedKeywordFormData;
  formErrors?: TrackedKeywordFormStateErrors;
  onChange: (value: string | string[] | number, name: FormStateOptions) => void;
  isEditView?: boolean;
  onCancel: () => void;
  onSubmit: () => Promise<void>;
  isSaveInProgress: boolean;
  isDisabled?: boolean;
  renderContentTop?: () => JSX.Element | null;
  category?: Category;
  onChangeCategory?: (value: Category) => void;
}

export const CreateOrUpdateTrackedKeywordForm = ({
  isEditView = false,
  onCancel,
  onSubmit,
  isSaveInProgress,
  isDisabled,
  renderContentTop,
  formState,
  formErrors,
  onChange,
  category,
  onChangeCategory,
}: CreateOrUpdateTrackedKeywordFormProps) => {
  const headRef = useRef<HTMLDivElement | null>(null);

  const isSubmitDisabled =
    !formState.displayName || !formState.description || isDisabled;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    if (isSubmitDisabled) {
      return;
    }
    e.preventDefault();
    onSubmit();
  };

  const shouldRenderCategoryInput = !!onChangeCategory;

  return (
    <Stack
      sx={{ p: 2, height: '100%' }}
      component="form"
      onSubmit={handleSubmit}
    >
      <Stack
        ref={headRef}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="body1">
          {isEditView ? 'Edit Tracked Keyword' : 'Create Tracked Keyword'}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <CanAccess
            resource={PermissionResource.Taxonomy}
            operation={PermissionOperation.Write}
          >
            <LoadingButton
              color="primary"
              variant="contained"
              type="submit"
              size="small"
              loading={isSaveInProgress}
              disabled={isSubmitDisabled}
            >
              Save
            </LoadingButton>
          </CanAccess>
        </Stack>
      </Stack>
      {renderContentTop?.()}
      <Stack
        spacing={3}
        sx={{
          height: `calc(100% - ${headRef.current?.offsetHeight}px)`,
          overflowY: 'auto',
          pt: 3,
        }}
      >
        <Stack direction="row" alignItems="center" width="100%">
          {shouldRenderCategoryInput && (
            <SelectCategoryMenu
              value={category}
              onChange={onChangeCategory}
              sxProps={{ width: 300 }}
              shouldShowLabel
            />
          )}
          <TextField
            autoFocus
            size="small"
            value={formState.displayName}
            fullWidth
            variant="outlined"
            onChange={(event) => onChange(event.target.value, 'displayName')}
            error={!!formErrors?.displayName}
            helperText={formErrors?.displayName}
            label="Tracked Keyword Name"
            placeholder="Enter a name like a feature or aspect of your product"
            InputLabelProps={{ shrink: true }}
            disabled={isDisabled}
            sx={{
              ml: shouldRenderCategoryInput ? 2 : 0,
            }}
            required
          />
        </Stack>
        <TextField
          size="small"
          value={formState.description}
          fullWidth
          multiline
          placeholder="Describe the Tracked Keyword by detailing this product feature or aspect"
          rows={4}
          onChange={(event) => onChange(event.target.value, 'description')}
          error={!!formErrors?.description}
          helperText={formErrors?.description}
          label="Tracked Keyword Description"
          disabled={isDisabled}
          InputLabelProps={{ shrink: true }}
          required
        />
        <Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <TrackedKeywordFormPhrasesSVGIcon />
            <Typography variant="body2" color="text.secondary">
              Phrases
            </Typography>
          </Stack>
          <Stack pt={2} spacing={3}>
            <TrackedKeywordPhrasesControlLabel
              label={
                <Stack direction="row" spacing={1}>
                  <AddRounded fontSize="small" sx={{ opacity: 0.7 }} />
                  <Typography variant="body2" color="text.secondary">
                    Included Phrases
                  </Typography>
                </Stack>
              }
              tooltipContent={INCLUDED_PHRASES_TOOLTIP_CONTENT}
              phrasesCount={
                <Typography variant="body2">
                  ({formState.phrases.length})
                </Typography>
              }
              control={
                <InputTags
                  size="small"
                  name="phrases"
                  tags={formState.phrases}
                  error={!!formErrors?.phrases}
                  helperText={formErrors?.phrases}
                  placeholder="Enter comma-separated values"
                  onChange={(_, tags) => onChange(tags, 'phrases')}
                  disabled={isDisabled}
                />
              }
            />
            <TrackedKeywordPhrasesControlLabel
              label={
                <Stack direction="row" spacing={1}>
                  <RemoveRounded fontSize="small" sx={{ opacity: 0.7 }} />
                  <Typography variant="body2" color="text.secondary">
                    Excluded Phrases
                  </Typography>
                </Stack>
              }
              phrasesCount={
                <Typography variant="body2">
                  ({formState.excludedPhrases.length})
                </Typography>
              }
              tooltipContent={EXCLUDED_PHRASES_TOOLTIP_CONTENT}
              control={
                <InputTags
                  size="small"
                  name="phrases"
                  tags={formState.excludedPhrases}
                  placeholder="Enter comma-separated values"
                  onChange={(_, tags) => onChange(tags, 'excludedPhrases')}
                  disabled={isDisabled}
                />
              }
            />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
